import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { MemberProfile } from "@/components/members/MemberProfile";
import { MemberPayments } from "@/components/members/MemberPayments";
import { FamilyManagement } from "@/components/members/FamilyManagement";
import { MFASetup } from "@/components/auth/MFASetup";

const MemberPortal = () => {
  const { user, isMember } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [memberData, setMemberData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    loadMemberData();
  }, [user, navigate]);

  const loadMemberData = async () => {
    try {
      const { data, error } = await supabase
        .from("members")
        .select("*")
        .eq("user_id", user?.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      setMemberData(data);
    } catch (error: any) {
      console.error("Error loading member data:", error);
      toast({
        title: "Error",
        description: "Failed to load member data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto py-8">
          <p className="text-center">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">{t("member.portal.title")}</h1>
          {memberData && (
            <p className="text-lg text-muted-foreground">
              Welcome back, <span className="font-semibold text-foreground">{memberData.full_name}</span>!
            </p>
          )}
        </div>

        {!memberData ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("member.portal.welcome")}</CardTitle>
              <CardDescription>
                {t("member.portal.needregistration")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/member-registration")}>
                {t("member.portal.completeregistration")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="profile" className="space-y-4">
            <TabsList>
              <TabsTrigger value="profile">{t("member.portal.tabs.profile")}</TabsTrigger>
              <TabsTrigger value="payments">{t("member.portal.tabs.payments")}</TabsTrigger>
              <TabsTrigger value="family">{t("member.portal.tabs.family")}</TabsTrigger>
              <TabsTrigger value="security">{t("member.portal.tabs.security")}</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <MemberProfile 
                memberData={memberData} 
                onUpdate={loadMemberData} 
                key={memberData?.updated_at || memberData?.id}
              />
            </TabsContent>

            <TabsContent value="payments">
              <MemberPayments memberId={memberData.id} />
            </TabsContent>

            <TabsContent value="family">
              <FamilyManagement memberId={memberData.id} onUpdate={loadMemberData} />
            </TabsContent>

            <TabsContent value="security">
              <MFASetup 
                userId={user!.id} 
                userEmail={user!.email || ''} 
              />
            </TabsContent>
          </Tabs>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MemberPortal;
